import { useState, useRef } from "react";
import { FileUp, Loader2, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../contexts/AuthContext";
import { Modal } from "./SharedComponents";
import { UNITS } from "./constants";

const SVC_UNITS = ["un", "m", "m²", "m³", "kg", "gl", "hora", "día", "mes", "servicio"];

/**
 * BLOQUE 1 PARTE A — Importar ítems de un Pedido de Materiales desde Excel/PDF/imagen.
 * BLOQUE 43 PARTE B — el MISMO modal se reutiliza para el Pedido de Servicio
 * (mode="servicios"): cambia el endpoint, el campo de descripción y las columnas
 * (Descripción del servicio / UM / Cantidad / Sector / Frente). Usa IA (Gemini) en el
 * backend. NO importa precios en ninguno de los dos modos.
 */
export function ImportPMItemsModal({ open, onClose, onConfirm, mode = "materiales", frenteOptions = [] }) {
  const isSvc = mode === "servicios";
  const nameField = isSvc ? "description" : "material_name";
  const tid = isSvc ? "import-svc" : "import-pm";
  const units = isSvc ? SVC_UNITS : UNITS;

  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const fileInputRef = useRef(null);

  const reset = () => {
    setPreview(null);
    setFileName("");
    setUploading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setUploading(true);
    setPreview(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const url = isSvc
        ? `${API}/purchases/service-requests/import-items`
        : `${API}/purchases/requests/import-items`;
      const res = await axios.post(url, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const items = (res.data?.items || []).map(it => ({ ...it, checked: true }));
      setPreview(items);
      toast.success(`${items.length} ítem${items.length !== 1 ? "s" : ""} detectado${items.length !== 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo procesar el archivo");
      setFileName("");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const updateRow = (idx, field, value) => {
    setPreview(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const toggleRow = (idx) => {
    setPreview(prev => prev.map((r, i) => i === idx ? { ...r, checked: !r.checked } : r));
  };

  const handleConfirm = () => {
    const chosen = (preview || [])
      .filter(r => r.checked && String(r[nameField] || "").trim())
      .map(({ checked, ...rest }) => rest);
    if (!chosen.length) { toast.error("Seleccioná al menos un ítem para importar"); return; }
    onConfirm(chosen);
    reset();
  };

  const checkedCount = (preview || []).filter(r => r.checked).length;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isSvc ? "Importar Ítems de Servicio desde Excel/PDF" : "Importar Ítems desde Excel/PDF"}
      extraWide
    >
      {!preview ? (
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            {isSvc ? (
              <>
                Subí una planilla o presupuesto de servicios (Excel .xlsx/.xls, PDF o imagen) y la IA extraerá
                automáticamente las descripciones, cantidades, unidades y sectores.{" "}
                <strong>No se importan precios</strong> — esos se cargan después en Pedido a Proveedores.
              </>
            ) : (
              <>
                Subí una cotización o planilla (Excel .xlsx/.xls, PDF o imagen) y la IA extraerá automáticamente
                los materiales, cantidades, unidades y marcas. <strong>No se importan precios</strong> — esos se
                cargan después en Pedido a Proveedores.
              </>
            )}
          </p>
          <label
            className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl p-10 cursor-pointer hover:border-[#2E6DA4] hover:bg-blue-50/30 transition-colors"
            data-testid={`${tid}-dropzone`}
          >
            {uploading ? (
              <>
                <Loader2 size={28} className="text-[#2E6DA4] animate-spin" />
                <p className="text-sm text-gray-600 font-medium">Analizando "{fileName}" con IA...</p>
                <p className="text-[11px] text-gray-400">Esto puede tardar unos segundos, sobre todo con PDFs largos.</p>
              </>
            ) : (
              <>
                <FileUp size={28} className="text-gray-400" />
                <p className="text-sm text-gray-600 font-medium">Hacé clic para elegir un archivo</p>
                <p className="text-[11px] text-gray-400">.xlsx, .xls, .pdf, .png, .jpg — máx. 15MB</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.pdf,.png,.jpg,.jpeg,.webp"
              className="hidden"
              onChange={handleFileChange}
              disabled={uploading}
              data-testid={`${tid}-file-input`}
            />
          </label>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-emerald-600" />
              <p className="text-xs font-semibold text-emerald-800">
                {preview.length} ítem{preview.length !== 1 ? "s" : ""} detectado{preview.length !== 1 ? "s" : ""} en "{fileName}" — revisá y editá antes de confirmar
              </p>
            </div>
            <button type="button" onClick={reset} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1" data-testid={`${tid}-reset-btn`}>
              <Trash2 size={12} /> Cargar otro archivo
            </button>
          </div>

          {preview.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <AlertTriangle size={28} className="mx-auto mb-2" />
              <p className="text-sm">No se detectaron ítems válidos.</p>
            </div>
          ) : (
            <div className="max-h-[45vh] overflow-y-auto border border-gray-200 rounded-xl">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-gray-500 uppercase text-[10px]">
                    <th className="py-2 px-2 text-left w-8"></th>
                    <th className="py-2 px-2 text-left">{isSvc ? "Descripción del servicio" : "Descripción"}</th>
                    <th className="py-2 px-2 text-left w-24">Cant.</th>
                    <th className="py-2 px-2 text-left w-20">UM</th>
                    {isSvc ? (
                      <>
                        <th className="py-2 px-2 text-left w-32">Sector</th>
                        <th className="py-2 px-2 text-left w-40">Frente</th>
                      </>
                    ) : (
                      <th className="py-2 px-2 text-left w-32">Marca</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {preview.map((row, i) => (
                    <tr key={i} className={row.checked ? "" : "opacity-40"} data-testid={`${tid}-row-${i}`}>
                      <td className="py-1.5 px-2">
                        <input type="checkbox" checked={row.checked} onChange={() => toggleRow(i)} data-testid={`${tid}-row-check-${i}`} />
                      </td>
                      <td className="py-1.5 px-2">
                        <input value={row[nameField] || ""} onChange={e => updateRow(i, nameField, e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs" data-testid={`${tid}-row-desc-${i}`} />
                      </td>
                      <td className="py-1.5 px-2">
                        <input type="number" min="0" step="any" value={row.quantity}
                          onChange={e => updateRow(i, "quantity", Number(e.target.value))}
                          className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs text-center" data-testid={`${tid}-row-qty-${i}`} />
                      </td>
                      <td className="py-1.5 px-2">
                        <select value={row.unit} onChange={e => updateRow(i, "unit", e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-1 py-1 text-xs bg-white" data-testid={`${tid}-row-unit-${i}`}>
                          {!units.includes(row.unit) && <option value={row.unit}>{row.unit}</option>}
                          {units.map(u => <option key={u}>{u}</option>)}
                        </select>
                      </td>
                      {isSvc ? (
                        <>
                          <td className="py-1.5 px-2">
                            <input value={row.sector || ""} onChange={e => updateRow(i, "sector", e.target.value)}
                              className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs" data-testid={`${tid}-row-sector-${i}`} />
                          </td>
                          <td className="py-1.5 px-2">
                            <select value={row.front || ""} onChange={e => updateRow(i, "front", e.target.value)}
                              className="w-full border border-gray-200 rounded-lg px-1 py-1 text-xs bg-white" data-testid={`${tid}-row-front-${i}`}>
                              <option value="">— Frente —</option>
                              {frenteOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          </td>
                        </>
                      ) : (
                        <td className="py-1.5 px-2">
                          <input value={row.marca_recomendada || ""} onChange={e => updateRow(i, "marca_recomendada", e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs" />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={handleClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">
              Cancelar
            </button>
            <button type="button" onClick={handleConfirm} disabled={checkedCount === 0}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-50"
              data-testid={`${tid}-confirm-btn`}>
              Agregar {checkedCount} ítem{checkedCount !== 1 ? "s" : ""} al pedido
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
